import Navbar from "react-bootstrap/lib/Navbar";
import Nav from "react-bootstrap/lib/Nav";

import { useAuth } from "@stadium/services/auth/client";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import CartIcon from "@components/store/navigation/cart-icon";
import NavigationRightSection from "@components/store/navigation/right-section";
import DropdownToggleDemoMode from "@components/store/navigation/demo-mode-dropdown";
import { useStoreContext } from "@components/store/provider";
import { usePreviewModeContext } from "@contexts/preview-mode-context";
import useGoogleAnalyticsActionEvents from "@hooks/use-analytics-action-events";
import useAnalyticsActionEvents from "@stadium/services/analytics/hooks/use-action-events";
import CheckoutProvider from "@contexts/checkout-context";
import { CurrentUserContext } from "@contexts/current-user-provider";

import { STICKY_HEADER_REF } from "@stadium/ui/src/components/header";
import DefaultStadiumHeader from "@components/store/navigation/stadium-header";
import { ExternalButton } from "@sharedComponents/buttons";

import { isEshopStore } from "@helpers/store";
import { getEventName } from "@stadium/helpers/analytics";
import { useCreateShopContext } from "@contexts/create-shop-context";

import { getDefaultBuildYourOwnShopNumber } from "@stadium/redux-utils/selectors/configSelector";
import { useConfig } from "@stadium/hooks/lib/use-config";
import { useWorkspaceContext } from "@contexts/workspace-context";

import {
  getStoreNumber,
  getStoreName,
  getStoreType,
  getIsPricelessShop,
  getIsSinglePageShop,
  getIsLockerShop,
} from "@store/selectors/store";
import { getReceivedItemsCount } from "@selectors/cart";
import { getStadiumCatalogNumber } from "@selectors/config";
import { getMenu } from "@selectors/menu";
import { getBookACallCtaText } from "@stadium/shared/src/selectors/book-a-call";

import { externalRoutes } from "@constants/external-routes";
import {
  ALL_PRODUCTS,
  getNewShopCreationRoute,
  getPreviewTemplateRoute,
  getStoreBasePath,
} from "@constants/routes";
import { DEMO_MODE_STORE_CURRENCY_OPTIONS } from "@constants/store";
import { JUNE_SO } from "@stadium/services/analytics/constants/services";
import { PREVIEW_MODE, USE_TEMPLATE } from "@constants/june-so";

import GlobalProductSearch from "@sharedComponents/global-search/global-product-search";
import StoreMenu from "@components/store/menu";
import { GiftingCatalogTopBanner } from "@components/preview-catalog/banners";
import SelectedItemsCount from "@components/store/navigation/selected-items-count";
import BrandLogo from "@components/store/navigation/brand-logo";

import { SERVER_PHASE } from "@stadium/constants";
import { BYSTADIUM_BOOK_A_CALL } from "@stadium/helpers/src/app-routes";

import styles from "./main.module.scss";

const Navigation = ({ editingAllowed, children, isFetchingMenu }) => {
  const { getDashboardUrlWithCurrentWorkspaceSlug } = useWorkspaceContext();
  const storeName = useSelector(getStoreName);
  const storeNumber = useSelector(getStoreNumber);
  const storeType = useSelector(getStoreType);
  const buildYourOwnShopNumber = useConfig(getDefaultBuildYourOwnShopNumber);
  const isPricelessShop = useSelector(getIsPricelessShop);
  const receivedItemsCount = useSelector(getReceivedItemsCount);
  const catalogShopNumber = useConfig(getStadiumCatalogNumber);
  const isSinglePageShop = useSelector(getIsSinglePageShop);
  const isLockerShop = useSelector(getIsLockerShop);
  const bookACallCtaText = useConfig(getBookACallCtaText);
  const { isLoading: isLoadingAuth, isAuthenticated } = useAuth();
  const { currentUserData } = useContext(CurrentUserContext);

  const isLoadingUserProfileNav = isLoadingAuth || (isAuthenticated && !currentUserData?.email);

  const { isNewShopCreationFlow, setDummyStoreReturnUrl } = useCreateShopContext();

  const [expanded, setExpanded] = useState(false);
  const [demoDropdownOpen, setDemoDropdownOpen] = useState(false);
  const { isMobileDevice, isTabletDevice } = useContext(CurrentDeviceContext);

  const {
    withMenu,
    withSearch,
    withCart,
    isPreviewCatalogMode,
    withCatalogMode,
    countryCode,
    lang,
    storeCode,
    workspaceSlug,
    isNewRoute,
  } = useStoreContext();
  const { previewMode, resetPreviewMode, setPreviewModeCurrency, previewModeCurrency } =
    usePreviewModeContext();
  const orderedMenuItem = useSelector(getMenu);
  const menuList = orderedMenuItem?.children || [];
  const menuListLength = menuList.length;
  const hideMenu = menuListLength <= 1 && menuList?.[0]?.code === ALL_PRODUCTS;

  const homePageRoute = getStoreBasePath({
    countryCode,
    lang,
    storeCode,
    storeNumber,
    workspaceSlug,
    isNewShopCreationFlow,
    isNewRoute,
  });
  const { trackShopPreview } = useGoogleAnalyticsActionEvents();

  const services = [JUNE_SO];
  const { trackEvent } = useAnalyticsActionEvents(services);

  const { currentUserData: { company = {} } = {} } = useContext(CurrentUserContext);

  useEffect(() => {
    if (previewMode && storeName && storeNumber) {
      const eventObj = { shop_id: storeNumber, shop_name: storeName };
      trackShopPreview(eventObj);
    }
  }, [previewMode, storeName, storeNumber]);

  useEffect(() => {
    if ((isMobileDevice || isTabletDevice) && expanded) {
      document.body.classList.add("stop-scroll");
    } else {
      document.body.classList.remove("stop-scroll");
    }
  }, [expanded, isMobileDevice, isTabletDevice]);

  const getUseTemplateRoute = () => {
    setDummyStoreReturnUrl(getPreviewTemplateRoute(homePageRoute));
    return getNewShopCreationRoute({
      countryCode,
      lang,
      storeCode,
      storeNumber: !isEshopStore(storeType) ? storeNumber : buildYourOwnShopNumber,
      workspaceSlug,
      isNewRoute,
    });
  };

  const handleCtaClick = () => {
    !isEshopStore(storeType) &&
      trackEvent({
        eventName: getEventName({
          pageName: PREVIEW_MODE,
          target: USE_TEMPLATE,
          action: "clicked",
        }),
        eventProperties: {
          shop_name: storeName,
          shop_number: storeNumber,
        },
        context: { groupId: company?.id },
        additionalProps: { companyName: company?.name },
      });
    resetPreviewMode(getUseTemplateRoute());
  };

  let className = ` ${styles.storeHeroTopNav} `;
  className += previewMode ? "" : styles["dNone"];
  let navGroupClassname = `${styles.navGroup} `;
  navGroupClassname += storeNumber && withMenu ? "withMenuList " : "withOutMenuList ";
  navGroupClassname += previewMode ? styles["previewModeOn"] : " ";
  navGroupClassname += isPreviewCatalogMode ? styles["previewCatalogModeNav"] : "";

  const backToTemplateHandler = () => {
    setTimeout(function () {
      resetPreviewMode(getDashboardUrlWithCurrentWorkspaceSlug(externalRoutes.chooseTemplates()));
    }, 500);
  };

  const renderDemoModeLinks = () => (
    <>
      <div className={styles.fixedBarRightBlock}>
        <div className={styles.fixedBarCurrencyBlock}>
          {Object.values(DEMO_MODE_STORE_CURRENCY_OPTIONS).map((currency) => {
            const { currencyCode, currencyDisplayName } = currency;
            const isActive = previewModeCurrency?.currencyCode === currencyCode;
            return (
              <span
                className={`${isActive ? styles.active : ""} ${styles.fixedBarCurrencyBtns}`}
                onClick={() => setPreviewModeCurrency(currency)}
                key={currencyCode}
              >
                {currencyDisplayName}
              </span>
            );
          })}
        </div>
      </div>
      <div className={styles.fixedBarRightBlockBtnHolder}>
        <div className={styles.btnCreateStore} onClick={handleCtaClick}>
          {!isEshopStore(storeType) ? "USE TEMPLATE" : "CREATE SHOP"}
        </div>
      </div>
    </>
  );

  const backToTemplatesCta = () => (
    <>
      <span className={styles.btnLink} onClick={backToTemplateHandler}>
        <i className={styles.iconArrowLeft} />
        <span className={styles.backToTemplate}>Back To Templates</span>
      </span>
      <div className={styles.backTemplateDemoMessage}>You're currently in demo mode</div>
    </>
  );

  const renderDesktopDemoModeBar = () => (
    <>
      <div className={`${styles.colAuto} ${styles.fixedBarBackBlock}`}>{backToTemplatesCta()}</div>
      <div className={styles.fixedBarToggleBlock}>
        <DropdownToggleDemoMode
          setDemoDropdownOpen={setDemoDropdownOpen}
          demoDropdownOpen={demoDropdownOpen}
        />
      </div>
      {renderDemoModeLinks()}
    </>
  );

  const renderMobileDemoModeBar = () => (
    <>
      {backToTemplatesCta()}
      <div className={styles.fixedBarToggleBlock}>
        <DropdownToggleDemoMode
          setDemoDropdownOpen={setDemoDropdownOpen}
          demoDropdownOpen={demoDropdownOpen}
        />
        {demoDropdownOpen && renderDemoModeLinks()}
      </div>
    </>
  );

  const openDemoModeBar = () => isMobileDevice && setDemoDropdownOpen(!demoDropdownOpen);
  const isMobileOrTablet = isMobileDevice || isTabletDevice;
  const showMenu =
    storeNumber &&
    withMenu &&
    catalogShopNumber !== storeNumber &&
    !isSinglePageShop &&
    !isFetchingMenu &&
    !hideMenu;

  return (
    <>
      {isPreviewCatalogMode && (
        <>
          <DefaultStadiumHeader
            isWeglotDropDown={!withCatalogMode}
            isMarketingPage={isPreviewCatalogMode}
            showUserFunds={!isPreviewCatalogMode}
          />
          {withCatalogMode && <GiftingCatalogTopBanner />}
        </>
      )}

      <div className={navGroupClassname} ref={STICKY_HEADER_REF}>
        <Navbar
          {...(Navbar.defaultProps || {})}
          fluid
          collapseOnSelect
          className={styles.topNavBar}
          expanded={expanded}
        >
          {!isPreviewCatalogMode && (
            <Navbar.Header {...(Navbar.Header.defaultProps || {})} className={styles.navBarWithLogo}>
              <div className={`${styles.navContainer} ${styles.pxMd} ${styles.dFlexContainer}`}>
                <Navbar.Brand {...(Navbar.Brand.defaultProps || {})}>
                  <BrandLogo className={styles.mrgnClass} />
                </Navbar.Brand>

                {withSearch && <GlobalProductSearch />}

                <div
                  className={`${styles.navRightSection} ${
                    withCart ? styles.withCart : styles.withoutCart
                  } ${isLoadingUserProfileNav || SERVER_PHASE ? styles.hideOnAuthLoading : ""}`}
                >
                  {isLockerShop && (
                    <ExternalButton
                      href={BYSTADIUM_BOOK_A_CALL}
                      styleType="primary"
                      className={styles.webSpeakToExpert}
                    >
                      {bookACallCtaText}
                    </ExternalButton>
                  )}

                  {!isMobileOrTablet && (
                    <NavigationRightSection expanded={expanded} setExpanded={setExpanded} />
                  )}
                </div>
                {isPricelessShop && receivedItemsCount > 0 && isMobileOrTablet && (
                  <CheckoutProvider skipPaymentOptions>
                    <SelectedItemsCount />
                  </CheckoutProvider>
                )}
                {withCart && (
                  <div className={`${styles.colAuto} ${styles.pr0}`}>
                    <CheckoutProvider skipPaymentOptions>
                      <CartIcon />
                    </CheckoutProvider>
                  </div>
                )}
                <Navbar.Toggle
                  {...(Navbar.Toggle.defaultProps || {})}
                  className={`${styles.navBarToggle} t-bg-4`}
                  onClick={() => setExpanded(expanded ? false : "expanded")}
                >
                  <i className={styles.iconMenu} />
                </Navbar.Toggle>
              </div>
            </Navbar.Header>
          )}
          <Navbar.Collapse
            {...(Navbar.Collapse.defaultProps || {})}
            className={styles.navRow}
            style={{ "--sticky-header-height": `${STICKY_HEADER_REF.current?.clientHeight}px` }}
          >
            {showMenu && (
              <Nav {...(Nav.defaultProps || {})} className={styles.storeNavigator}>
                <StoreMenu
                  storeNumber={storeNumber}
                  navContainerClass={styles.storeNavigationContainer}
                  setMenuVisibility={setExpanded}
                  expanded={expanded}
                />
                {editingAllowed && children}
              </Nav>
            )}
            <div className={styles.navCollapseRightSection}>
              {isMobileOrTablet && (
                <NavigationRightSection expanded={expanded} setExpanded={setExpanded} />
              )}
            </div>
          </Navbar.Collapse>
        </Navbar>
      </div>
      <div className={`${className} ${demoDropdownOpen ? styles.open : ""}`}>
        <div className={`${styles.navContainer} ${styles.justifyContent}`}>
          {isMobileDevice ? renderMobileDemoModeBar() : renderDesktopDemoModeBar()}
        </div>

        <div className={styles.demoMobileClickable} onClick={openDemoModeBar} />
      </div>
    </>
  );
};

export default Navigation;
